import { useEffect, useRef } from 'react';
import { REACT_APP_NAME } from 'src/utils/constants/constants';
import {
  ActionOutboxEntityType,
  CatalogResults,
  ClientLogRequest,
  LogLevel,
} from 'src/WebApiController';

import { ExpandedEventData } from './CatalogDataContext.types';

interface CountMismatch {
  eventId: string;
  countField: string;
  expectedCount: number;
  actualCount: number;
  difference: number;
}

export function useCatalogCountMismatchLogger(
  entityType: ActionOutboxEntityType,
  catalogResults: CatalogResults | undefined,
  expandedData: ExpandedEventData | null | undefined,
  eventsFullyLoaded: boolean | never[],
  viewMode: string | null | undefined,
  mainRoute: string,
  logEvent: (event: ClientLogRequest) => void
): void {
  // Track which catalogResults reference we've already logged for
  const lastLoggedCatalogRef = useRef<CatalogResults | null>(null);

  useEffect(() => {
    if (!eventsFullyLoaded || !catalogResults || !expandedData) {
      return;
    }

    // Only log once per catalogResults reference
    if (lastLoggedCatalogRef.current === catalogResults) {
      return;
    }

    const mismatches: CountMismatch[] = [];

    for (const eventWithData of Object.values(catalogResults.events)) {
      const counts = eventWithData.counts;
      const eventId = counts?.viagVirtId;

      if (!eventId || !counts || !expandedData[eventId]) {
        continue;
      }

      const expandedEvent = expandedData[eventId];

      let expectedCount: number | null | undefined;
      let actualCount: number | undefined;
      let countField: string;

      switch (entityType) {
        case ActionOutboxEntityType.Listing:
          expectedCount = counts.listCnt;
          actualCount = expandedEvent.listings?.length;
          countField = 'listCnt';
          break;
        case ActionOutboxEntityType.Sale:
          expectedCount = counts.salesCnt;
          actualCount = expandedEvent.sales?.length;
          countField = 'salesCnt';
          break;
        case ActionOutboxEntityType.Purchase:
          expectedCount = counts.tktGrpsCnt;
          actualCount = expandedEvent.ticketGroups?.length;
          countField = 'tktGrpsCnt';
          break;
        default:
          continue;
      }

      if (
        expectedCount != null &&
        actualCount != null &&
        expectedCount !== actualCount
      ) {
        mismatches.push({
          eventId,
          countField,
          expectedCount,
          actualCount,
          difference: expectedCount - actualCount,
        });
      }
    }

    // Mark this catalogResults as logged regardless of whether mismatches exist
    lastLoggedCatalogRef.current = catalogResults;

    if (mismatches.length === 0) {
      return;
    }

    // Single log entry with all mismatches
    logEvent({
      logLevel: LogLevel.Warning,
      applicationSource: REACT_APP_NAME,
      errorId: 'CatalogCountMismatch',
      error: `${mismatches.length} count mismatch(es) detected across ${Object.keys(catalogResults.events).length} events`,
      pageUrl: window.location.href,
      componentStack: null,
      timestamp: new Date().toISOString(),
      additionalInfo: {
        entityType,
        totalEventsChecked: Object.keys(catalogResults.events).length,
        totalMismatches: mismatches.length,
        mismatches,
        viewMode,
        route: mainRoute,
      },
    });
  }, [
    eventsFullyLoaded,
    catalogResults,
    expandedData,
    entityType,
    logEvent,
    viewMode,
    mainRoute,
  ]);
}
